import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SUPPORT_EMAIL, SUPPORT_HREF } from '@/shell/footer/service-links';

export type ServiceSection = {
  readonly id: string;
  readonly title: string;
  readonly paragraphs: readonly string[];
  readonly items?: readonly string[];
};

/**
 * The shared body of the footer's two service pages.
 *
 * THEIR CONTENT IS PROVISIONAL, AND EACH PAGE SAYS SO AT THE TOP (the Human, 2026-10-02: «per il
 * momento scrivi dei testi plausibili con una nota che indica che sono temporanei»). The note is
 * words, not a badge, so nobody takes the page for the final guide or for a live status board. The
 * text names nothing the console does not do, and it ends with the one thing a person who came here
 * can always use: the support address. Replacing the texts later changes the two callers' strings
 * and nothing around them.
 */
export function ServicePage({
  title,
  summary,
  note,
  sections,
}: {
  readonly title: string;
  readonly summary: string;
  readonly note: string;
  readonly sections: readonly ServiceSection[];
}) {
  const { t } = useTranslation('shell');

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="screen-title">{title}</h2>
        <p className="screen-summary">{summary}</p>
        <p role="note" className="text-muted-foreground flex items-start gap-2 text-sm">
          <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          {note}
        </p>
      </div>
      {sections.map((section) => (
        <Card key={section.id}>
          <CardHeader>
            <CardTitle asChild className="card-label">
              <h3>{section.title}</h3>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm leading-6">
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {section.items === undefined ? null : (
              <ul className="list-disc space-y-1.5 pl-5">
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ))}
      <p className="text-muted-foreground text-sm">
        {t('servicePages.contact')}{' '}
        <a className="text-link font-semibold" href={SUPPORT_HREF}>
          {SUPPORT_EMAIL}
        </a>
      </p>
    </div>
  );
}
